import { useMutation } from '@apollo/client';
import { useTranslation } from '@crehana/i18n';
import { useMe } from '@crehana/web';

import useAdminDashboard from '@/views/AdminDashboard/hooks/useAdminDashboard';

import useReportsAmplitude from '../useReportsAmplitude';
import GenerateCoursesReportMutation from './graphql/GenerateCoursesReport.v4.b2b.graphql';
import type {
  GenerateCoursesReport as GenerateReport,
  GenerateCoursesReportVariables as GenerateReportVariables,
} from './graphql/types/GenerateCoursesReport';

enum STATUS {
  SUCCESS = 'success',
  FAILED = 'error',
}

const useSendCoursesReport = (slug: string) => {
  const { t } = useTranslation();
  const { me } = useMe();
  const { openSnackbar } = useAdminDashboard();
  const { logSubmit } = useReportsAmplitude(slug);
  const [generateCoursesReport] = useMutation<
    GenerateReport,
    GenerateReportVariables
  >(GenerateCoursesReportMutation, {
    context: { clientName: 'v4.b2b' },
    onCompleted({ generateCourseReport }) {
      // biome-ignore lint/style/useBlockStatements: single-line early return
      if (!generateCourseReport?.success)
        openSnackbar({
          type: 'alert',
          msg: t('SNACKBAR_DEFAULT_ERROR'),
        });
    },
    onError() {
      openSnackbar({
        type: 'alert',
        msg: t('SNACKBAR_DEFAULT_ERROR'),
      });
    },
  });

  const submitReport = async (variables: GenerateReportVariables) => {
    const { data } = await generateCoursesReport({
      variables: {
        ...variables,
        userId: me?.originalId || -1,
      },
    });
    logSubmit({
      event: 'Generate Courses Report',
      reportType: 'Courses Report',
      filters: variables,
      status: data?.generateCourseReport?.success
        ? STATUS.SUCCESS
        : STATUS.FAILED,
    });
  };

  return {
    submitReport,
  };
};

export default useSendCoursesReport;
